import { useCallback, useEffect, useRef, useState } from "react";
import BriefingsIndexContentSection, {
  type BriefingsStatusFilter,
} from "@/components/scheduled-briefings/briefings-index/BriefingsIndexContentSection";
import BriefingsIndexEmptyView from "@/components/scheduled-briefings/briefings-index/BriefingsIndexEmptyView";
import BriefingsIndexHeader from "@/components/scheduled-briefings/briefings-index/BriefingsIndexHeader";
import BriefingsTable from "@/components/scheduled-briefings/briefings-index/briefings-table/BriefingsTable";
import { briefingsIndexSummaryDescription } from "@/components/scheduled-briefings/briefings-index/briefingsIndexSummary";
import type { BriefingTableTabCounts } from "@/data-access/scheduled-briefings/queries/briefing-table-queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFeature } from "@/lib/feature-management";

const EMPTY_TAB_COUNTS: Record<BriefingsStatusFilter, number> = {
  all: 0,
  NEEDS_REVIEW: 0,
  ACTIVE: 0,
  SCHEDULED: 0,
  PAUSED: 0,
};

const BriefingsIndexRoute = () => {
  const showStatus = useFeature("enable-scheduled-briefings-v1-1", false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] =
    useState<BriefingsStatusFilter>("all");
  const activeStatus = showStatus ? selectedStatus : "all";
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] =
    useState<Record<BriefingsStatusFilter, number>>(EMPTY_TAB_COUNTS);
  /** `allCount` honors search, so the empty view only trusts counts from unsearched responses. */
  const [unsearchedAllCount, setUnsearchedAllCount] = useState<number | null>(
    null,
  );

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const debouncedSearchTermRef = useRef(debouncedSearchTerm);
  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);
  const isFiltered =
    debouncedSearchTerm.trim().length > 0 || activeStatus !== "all";
  const hasNoBriefings = !error && !isFiltered && unsearchedAllCount === 0;
  const briefingCount = unsearchedAllCount ?? tabCounts.all;

  const handlePageLoaded = useCallback(
    (payload: { totalCount: number; tabCounts: BriefingTableTabCounts }) => {
      setTotalCount(payload.totalCount);
      setTabCounts({
        all: payload.tabCounts.allCount,
        NEEDS_REVIEW: payload.tabCounts.needsReviewCount,
        ACTIVE: payload.tabCounts.activeCount,
        SCHEDULED: payload.tabCounts.scheduledCount,
        PAUSED: payload.tabCounts.pausedCount,
      });
      if (!debouncedSearchTermRef.current.trim()) {
        setUnsearchedAllCount(payload.tabCounts.allCount);
      }
      setIsInitialLoading(false);
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedStatus("all");
  }, []);

  const handleClearError = useCallback(() => {
    setError(null);
  }, []);

  const handleErrorChange = useCallback((message: string | null) => {
    setError(message);
    if (message) {
      setIsInitialLoading(false);
    }
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <BriefingsIndexHeader
        briefingCount={briefingCount}
        description={briefingsIndexSummaryDescription(briefingCount)}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
      />
      <BriefingsIndexContentSection
        activeStatus={activeStatus}
        onStatusChange={setSelectedStatus}
        statusCounts={tabCounts}
        showStatus={showStatus}
      >
        {hasNoBriefings ? (
          <BriefingsIndexEmptyView />
        ) : (
          <BriefingsTable
            searchTerm={debouncedSearchTerm}
            statusTab={activeStatus}
            showStatus={showStatus}
            isLoading={isLoading}
            error={error}
            totalCount={totalCount}
            isInitialLoading={isInitialLoading}
            isFiltered={isFiltered}
            onClearFilters={handleClearFilters}
            onClearError={handleClearError}
            onLoadingChange={setIsLoading}
            onErrorChange={handleErrorChange}
            onPageLoaded={handlePageLoaded}
          />
        )}
      </BriefingsIndexContentSection>
    </div>
  );
};

export default BriefingsIndexRoute;
